import { api } from "./api";

import type { GroupingSource } from "./metadoc-api";

export type DocumentGroup = {
  id: number;
  metadoc: number;
  start_pg: number;
  length: number;
  end_pg: number;
  document_type: string;
  provider: string | null;
  facility: string | null;
  raw_provider_and_facility: string | null;
  date: string | null;
  created: string;
  source: GroupingSource;
  is_cloned?: boolean;
  background_job_id?: number;
};

export type CreateDocumentGroupArgs = {
  metadoc: number;
  start_pg: number;
  length: number;
  document_type: string;
  source?: GroupingSource;
};

export type UpdateDocumentGroupArgs = {
  id: number;
  start_pg?: number;
  length?: number;
  document_type?: string;
  date?: string | null;
  provider?: string | null;
  facility?: string | null;
};

export type BulkUpdateDocumentGroupItem = {
  id: number;
  document_type?: string;
  date?: string | null;
  provider?: string | null;
  facility?: string | null;
  delete?: boolean;
};

/** Field-level update payload shared by lazy pending state and bulk_update items. */
export type DocumentGroupFieldUpdate = Omit<BulkUpdateDocumentGroupItem, "id">;

export type BulkUpdateDocumentGroupsResponse = {
  background_job_id?: number;
};

const documentGroupApi = api.injectEndpoints({
  overrideExisting: true,
  endpoints: (build) => ({
    // create a new document group
    createDocumentGroup: build.mutation<DocumentGroup, CreateDocumentGroupArgs>(
      {
        query: (data) => ({
          url: "api/v1/document-groups/",
          method: "POST",
          body: data,
          headers: {
            "Content-Type": "application/json",
          },
        }),
        invalidatesTags: ["DocumentGroups"],
      },
    ),

    // get all document groups for a specific metadoc
    getDocumentGroups: build.query<DocumentGroup[], number>({
      query: (metadocId) => ({
        url: "api/v1/document-groups/",
        method: "GET",
        params: {
          metadoc: metadocId,
        },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: "DocumentGroups" as const,
                id,
              })),
              { type: "DocumentGroups", id: "LIST" },
            ]
          : [{ type: "DocumentGroups", id: "LIST" }],
    }),

    // update a document group
    updateDocumentGroup: build.mutation<DocumentGroup, UpdateDocumentGroupArgs>(
      {
        query: ({ id, ...data }) => ({
          url: `api/v1/document-groups/${id}/`,
          method: "PATCH",
          body: data,
          headers: {
            "Content-Type": "application/json",
          },
        }),
        invalidatesTags: (_result, _error, { id }) => [
          { type: "DocumentGroups", id },
          { type: "DocumentGroups", id: "LIST" },
        ],
      },
    ),

    // bulk update multiple document groups in a single request
    batchUpdateDocumentGroups: build.mutation<
      BulkUpdateDocumentGroupsResponse,
      BulkUpdateDocumentGroupItem[]
    >({
      query: (items) => ({
        url: "api/v1/document-groups/bulk_update/",
        method: "PATCH",
        body: items,
        headers: {
          "Content-Type": "application/json",
        },
      }),
      invalidatesTags: [{ type: "DocumentGroups", id: "LIST" }],
    }),

    // delete a document group
    deleteDocumentGroup: build.mutation<void, number>({
      query: (id) => ({
        url: `api/v1/document-groups/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "DocumentGroups", id },
        { type: "DocumentGroups", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useCreateDocumentGroupMutation,
  useGetDocumentGroupsQuery,
  useUpdateDocumentGroupMutation,
  useBatchUpdateDocumentGroupsMutation,
  useDeleteDocumentGroupMutation,
} = documentGroupApi;

export default documentGroupApi;
